import * as React from 'react';
import { useLayout } from '../../contexts/LayoutContext';

export interface LeftNavPaneProps {
  children: React.ReactNode;
}

/**
 * LeftNavPane - primary navigation sidebar
 *
 * Behavior (frontend-architecture.md §2, requirements):
 * - pinned to far left
 * - visible by default
 * - expanded by default
 * - collapsible via toggle chevron in header
 * - collapsed state shows icons only
 * - state persists in localStorage via LayoutContext
 */
export function LeftNavPane({ children }: LeftNavPaneProps) {
  const { leftNavCollapsed, toggleLeftNav } = useLayout();

  return (
    <aside
      className={`shell-leftnav ${leftNavCollapsed ? 'shell-leftnav--collapsed' : ''}`}
      aria-label="Primary navigation"
      data-collapsed={leftNavCollapsed || undefined}
    >
      <div className="leftnav-header">
        {!leftNavCollapsed && <h2 className="leftnav-header-title">Navigation</h2>}
        <button
          type="button"
          className="leftnav-header-toggle"
          onClick={toggleLeftNav}
          aria-label={leftNavCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!leftNavCollapsed}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {leftNavCollapsed ? (
              // Chevrons pointing right (>>)
              <>
                <path
                  d="M4 12l4-4-4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12l4-4-4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            ) : (
              // Chevrons pointing left (<<)
              <>
                <path
                  d="M12 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
          </svg>
        </button>
      </div>
      {children}
    </aside>
  );
}
